import { useMemo, useState } from "react";
import { ActionRow } from "../components/ActionRow";
import { BottomNav } from "../components/BottomNav";
import { BrandHeader } from "../components/BrandHeader";
import { EvidenceRow } from "../components/EvidenceRow";
import { Icon } from "../components/Icon";
import { StatusLabel } from "../components/StatusLabel";
import type { RouteView } from "../hooks/useHashRoute";
import type { BriefingSourceState } from "../hooks/useBriefing";
import type { UiBriefing } from "../types";

const BASE_FILTERS = ["Major stories", "US", "World", "Business", "Technology", "Science"];

export function BriefingView({ briefing, dataSource, refreshError, onRetry, onStory, onNavigate, onSettings, onBrandTap }: {
  briefing: UiBriefing; dataSource: BriefingSourceState; refreshError: string | null; onRetry: () => void; onStory: (id: string) => void; onNavigate: (view: RouteView) => void; onSettings: () => void; onBrandTap: () => void;
}) {
  const [filter, setFilter] = useState("Major stories");
  const filters = useMemo(() => Array.from(new Set([...BASE_FILTERS, ...briefing.stories.map((story) => story.category)])), [briefing.stories]);
  const visible = filter === "Major stories" ? briefing.stories : briefing.stories.filter((story) => story.category === filter);
  const lead = visible[0] ?? briefing.stories[0];
  return (
    <>
      <BrandHeader onSettings={onSettings} onBrandTap={onBrandTap} />
      <main className="screen screen--with-nav" id="main-content">
        <h1>Today’s briefing</h1>
        <p className="date-line">{briefing.dateLabel}</p>
        <aside className={`data-status data-status--${dataSource}`} aria-label="Briefing data status">
          <div><strong>{dataSource === "live" ? "LIVE RSS COVERAGE" : dataSource === "cache" ? "CACHED LIVE BRIEFING" : "DEMO MODE"}</strong><span>Generated {new Date(briefing.generatedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span></div>
          {briefing.pipeline ? <p>{briefing.pipeline.analyzedArticleCount} reports analyzed · {briefing.pipeline.successfulFeedCount}/{briefing.pipeline.feedCount} feeds reached</p> : null}
          {refreshError ? <button type="button" onClick={onRetry}>Refresh failed · retry</button> : null}
        </aside>
        <div className="filter-rail" role="tablist" aria-label="Story category">
          {filters.map((item) => <button type="button" role="tab" aria-selected={filter === item} className={filter === item ? "is-selected" : ""} key={item} onClick={() => setFilter(item)}>{item}</button>)}
        </div>
        {lead ? (
          <article className="lead-story">
            <div className="lead-story__rail" aria-hidden="true" />
            <div className="lead-story__content">
              <button type="button" className="story-title-button" onClick={() => onStory(lead.id)}><h2>{lead.headline}</h2></button>
              <StatusLabel status={lead.status} />
              <p className="story-meta">{lead.sourceCount} publications · {lead.independentChainCount} independent chains</p>
              <section className="dossier-section"><h3>The facts</h3>{lead.facts.slice(0, 3).map((fact) => <EvidenceRow key={fact.text}>{fact.text}</EvidenceRow>)}</section>
              {lead.unclear.length ? <section className="dossier-section dossier-section--uncertain"><h3>Still unclear</h3>{lead.unclear.slice(0, 1).map((item) => <EvidenceRow tone="uncertain" key={item}>{item}</EvidenceRow>)}</section> : null}
              <ActionRow onClick={() => onStory(lead.id)}>Compare coverage</ActionRow>
            </div>
          </article>
        ) : <div className="empty-state"><h2>No stories in this category</h2><p>Major stories remain available in the first tab.</p></div>}
        <section className="developing-list">
          <h2>Also developing</h2>
          {visible.slice(1).map((story) => (
            <button type="button" key={story.id} onClick={() => onStory(story.id)}>
              <span>{story.headline}</span><small className={story.status === "DEVELOPING" ? "is-uncertain" : ""}>{story.status}</small><Icon name="chevron" />
            </button>
          ))}
        </section>
      </main>
      <BottomNav selected="briefing" onNavigate={onNavigate} />
    </>
  );
}
